// Inline SVG icons for common operating systems (no external deps).
// Use brand-true colors so they look right on light/secondary backgrounds.
import type { CSSProperties } from 'react';

interface OsIconProps {
  code: string | undefined | null;
  size?: number;
  /** Render in monochrome (currentColor) instead of brand color. */
  mono?: boolean;
  style?: CSSProperties;
}

export function OsIcon({ code, size = 16, mono = false, style }: OsIconProps) {
  const key = (code ?? '').toLowerCase();
  if (!key) return <DefaultDisk size={size} style={style} mono={mono} />;

  if (/(^|[\W_])debian/.test(key)) return <Debian size={size} style={style} mono={mono} />;
  if (/(^|[\W_])ubuntu/.test(key)) return <Ubuntu size={size} style={style} mono={mono} />;
  if (/(^|[\W_])cent[\s\-_]?os|^centos/.test(key) || /rocky|alma/.test(key))
    return <CentOSLike size={size} style={style} mono={mono} />;
  if (/(^|[\W_])fedora/.test(key)) return <Fedora size={size} style={style} mono={mono} />;
  if (/(^|[\W_])windows|^win\d/.test(key)) return <Windows size={size} style={style} mono={mono} />;
  if (/(^|[\W_])arch/.test(key)) return <Arch size={size} style={style} mono={mono} />;
  if (/(^|[\W_])alpine/.test(key)) return <Alpine size={size} style={style} mono={mono} />;

  return <DefaultDisk size={size} style={style} mono={mono} />;
}

function svgProps(size: number, style?: CSSProperties) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg',
    style: { display: 'inline-block', verticalAlign: 'middle', ...style },
  } as const;
}

const colors = {
  debian: '#A81D33',
  ubuntu: '#E95420',
  centos: '#932279',
  fedora: '#294172',
  windows: '#0078D6',
  arch: '#1793D1',
  alpine: '#0D597F',
};

function Debian({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.debian;
  return (
    <svg {...svgProps(size, style)}>
      <path
        fill={c}
        d="M13.88 12.13c-.39 0 .08.2.59.28.14-.11.27-.22.39-.33-.32.08-.65.08-.98.05zm2.1-.52c.23-.32.4-.67.46-1.04-.05.24-.18.45-.31.66-.7.44-.07-.26 0-.53-.76.95-.1.57-.15.84zm.77-2.01c.05-.7-.13-.48-.2-.21.08.04.14.55.2.21zM12.43.36c.21.04.45.06.42.12.23-.05.28-.1-.42-.12zm.42.12-.15.03.14-.01v-.02zM19.1 9.74c.02.61-.18 1-.36 1.42l-.32.16c-.27.52.03.33-.16.74-.42.36-1.27 1.14-1.55 1.21-.2 0 .13-.24.17-.33-.55.37-.44.56-1.27.79l-.03-.06c-2.04.96-4.88-.95-4.84-3.55-.02.17-.07.13-.12.2-.11-1.41.65-2.81 1.93-3.39 1.26-.62 2.74-.37 3.65.48-.5-.65-1.49-1.35-2.66-1.28-1.16.01-2.23.74-2.59 1.55-.59.37-.66 1.44-.92 1.63-.36 2.62.67 3.75 2.41 5.08.27.18.07.21.11.34-.58-.27-1.11-.68-1.55-1.18.23.34.48.67.81.93-.55-.19-1.29-1.34-1.51-1.39.95 1.71 3.86 3 5.39 2.36-.71.03-1.61.01-2.41-.28-.33-.17-.79-.53-.71-.6 2.05.76 4.16.58 5.93-.84.45-.35.94-.94 1.08-.95-.22.32.04.16-.13.44.46-.74-.2-.3.47-1.27l.25.34c-.09-.61.76-1.36.67-2.33.2-.3.22.32 0 1-.31.71.05.25.1.37.1-.27.32-.49.43-.97-.06-.27-.16.13-.26-.46.13-.34.11-.06.16-.24-.16-.09-.57-.7-.83-1.24.18-.27.47.36.71.38-.15-.86-.4-.41-.59-1.18.05-.02.13.24.21.36-.05-.31-.14-.6-.14-.86-.27-.55-.09.08-.31-.24-.28-.88.22-.21.26-.61.43.61.66 1.57.76 2 -.04-.21-.1-.43-.16-.65zm-4.62 3.78c-.04.16.1.06.16.04-.02-.03-.02-.13-.16-.04zm.88-.21c-.06-.18-.43-.04-.32.06.21.04.22-.06.32-.06zM10.4 1.16c.23.04.49.07.45.13.25-.05.31-.1-.45-.13zm.45.13-.16.04.15-.01v-.03z"
      />
    </svg>
  );
}

function Ubuntu({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.ubuntu;
  return (
    <svg {...svgProps(size, style)}>
      <circle cx="12" cy="12" r="9.5" fill="none" stroke={c} strokeWidth="2" />
      <circle cx="4.5" cy="12" r="1.8" fill={c} />
      <circle cx="15.6" cy="5.5" r="1.8" fill={c} />
      <circle cx="15.6" cy="18.5" r="1.8" fill={c} />
    </svg>
  );
}

function CentOSLike({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.centos;
  return (
    <svg {...svgProps(size, style)}>
      <rect x="3" y="3" width="8.4" height="8.4" rx="1" fill={c} />
      <rect x="12.6" y="3" width="8.4" height="8.4" rx="1" fill="#262577" />
      <rect x="3" y="12.6" width="8.4" height="8.4" rx="1" fill="#EFA724" />
      <rect x="12.6" y="12.6" width="8.4" height="8.4" rx="1" fill="#9CCD2A" />
    </svg>
  );
}

function Fedora({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.fedora;
  return (
    <svg {...svgProps(size, style)}>
      <circle cx="12" cy="12" r="9.5" fill={c} />
      <path
        fill="#fff"
        d="M14.6 6.8c-1.6 0-2.9 1.3-2.9 2.9v2.8h-2.1v2.1h2.1v2.5h2.1V14.6h2.1v-2.1h-2.1V9.9c0-.4.3-.8.7-.8h2.1V7h-2z"
      />
    </svg>
  );
}

function Windows({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.windows;
  return (
    <svg {...svgProps(size, style)}>
      <path fill={c} d="M3 4.6 11 3.5v8.2H3V4.6zm0 8.2h8v8.2L3 19.9v-7.1zm9-9.4 9-1.4v9.9h-9V3.4zm0 9.4h9V21.4l-9-1.4v-7.2z" />
    </svg>
  );
}

function Arch({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.arch;
  return (
    <svg {...svgProps(size, style)}>
      <path fill={c} d="M12 2 4 21h3.2l.9-2.3h7.8l.9 2.3H20L12 2zm-2.5 14L12 9.4l2.5 6.6h-5z" />
    </svg>
  );
}

function Alpine({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : colors.alpine;
  return (
    <svg {...svgProps(size, style)}>
      <path
        fill={c}
        d="M2.5 18 8 9l4 6.6 1.2-1.8L17 19l-2-3.4L21.5 18H2.5z"
      />
      <circle cx="12" cy="6" r="1.8" fill={c} />
    </svg>
  );
}

function DefaultDisk({ size, style, mono }: { size: number; style?: CSSProperties; mono: boolean }) {
  const c = mono ? 'currentColor' : 'var(--g-500)';
  return (
    <svg {...svgProps(size, style)}>
      <circle cx="12" cy="12" r="9" fill="none" stroke={c} strokeWidth="2" />
      <circle cx="12" cy="12" r="2.5" fill={c} />
    </svg>
  );
}

function pickCode(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.code === 'string') return o.code;
  }
  return undefined;
}
export { pickCode };
